import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type CompanyProfileResponse,
  errorMessage,
  getCompanyProfileQueryKey,
  previewCompanyContextQueryKey,
  upsertCompanyProfileMutation,
} from "@/api";
import { useAppForm } from "@/components/form/form";
import { BrandingSection } from "./BrandingSection";
import { companySchema, formToBody, profileToForm } from "./companyForm";
import { IdentitySections } from "./IdentitySections";
import { KnowledgeSections } from "./KnowledgeSections";
import { useUploadLogo } from "./useCompanyLogo";

/** The single long company-profile form with one sticky SaveBar. */
export function CompanyProfileForm({ profile }: Readonly<{ profile: CompanyProfileResponse | null }>) {
  const { t } = useTranslation("adminCompany");
  const toast = useToast();
  const queryClient = useQueryClient();
  const upsert = useMutation(upsertCompanyProfileMutation());
  const uploadLogo = useUploadLogo();
  // A logo picked before the profile exists; uploaded right after it's created.
  const [pendingLogo, setPendingLogo] = useState<File | null>(null);

  const uploadPendingLogo = async () => {
    if (!pendingLogo) return;
    try {
      await uploadLogo.mutateAsync({ body: { file: pendingLogo }, throwOnError: true });
      setPendingLogo(null);
    } catch (error) {
      toast.error(errorMessage(error, t("logo.failed")));
    }
  };

  const form = useAppForm({
    defaultValues: profileToForm(profile),
    validators: { onChange: companySchema(t) },
    onSubmit: async ({ value }) => {
      try {
        await upsert.mutateAsync({ body: formToBody(value), throwOnError: true });
        await uploadPendingLogo();
        form.reset(value);
        toast.success(profile ? t("saved") : t("created"));
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getCompanyProfileQueryKey() }),
          queryClient.invalidateQueries({ queryKey: previewCompanyContextQueryKey() }),
        ]);
      } catch (error) {
        toast.error(errorMessage(error, t("saveFailed")));
      }
    },
  });

  return (
    <form
      aria-label={t("formLabel")}
      className="flex flex-col gap-10"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <IdentitySections form={form} />
      <KnowledgeSections form={form} />
      <BrandingSection
        form={form}
        hasProfile={Boolean(profile)}
        hasLogo={Boolean(profile?.logo_url)}
        pendingLogo={pendingLogo}
        onPendingLogo={setPendingLogo}
      />
      <form.AppForm>
        <form.SubmitActions onCancel={() => form.reset()} submitLabel={profile ? t("save") : t("create")} />
      </form.AppForm>
    </form>
  );
}
